/**
 * 坐标转换工具层：基于 gcoord 统一 WGS84 / GCJ02 / BD09 互转，
 * 兼容历史百度枚举 1=WGS84、3=GCJ02、5=BD09 及 getGeoconvMultiterm / wgsTobd 等签名。
 * @see https://github.com/hujiulong/gcoord
 */
import gcoord from 'gcoord'

/** 历史百度 getGeoconv：x=经度、y=纬度；附带 lat/lon 便于对象型历史写法 */
export type GeoconvPoint = { x: number; y: number; lat: number; lon: number }

/**
 * [ lon, lat ] 与 `{ lon, lat }` 合一：下标与对象两种写法都可用（兼容历史）。
 */
export type LatLonTuple = [number, number] & { lat: number; lon: number }

function tupleLatLon(lat: number, lon: number): LatLonTuple {
  const t = [lon, lat] as [number, number]
  return Object.assign(t, { lat, lon }) as LatLonTuple
}

export type GetGeoconvMultitermParams = {
  coords: string
  from?: number
  to?: number
}

function legacyCrsToGcoord(code: number) {
  if (code === 1) {
    return gcoord.WGS84
  }
  if (code === 3) {
    return gcoord.GCJ02
  }
  if (code === 5) {
    return gcoord.BD09
  }
  throw new Error(`geo-coord: unsupported legacy CRS code ${code} (expected 1, 3, or 5)`)
}

/**
 * 单点经纬度转换（经度、纬度）；任意 1/3/5 组合由 gcoord 内部处理中转。
 */
export function transformLegacyLonLat(
  lon: number,
  lat: number,
  from: number,
  to: number
): [number, number] {
  if (from === to) {
    return [lon, lat]
  }
  const pair = gcoord.transform(
    [lon, lat],
    legacyCrsToGcoord(from),
    legacyCrsToGcoord(to)
  ) as [number, number]
  return [pair[0], pair[1]]
}

/**
 * 批量：coords 为「经度,纬度」分号拼接；每项含 `x/y` 与 `lon/lat`（语义一致）
 */
export async function getGeoconvMultiterm({
  coords,
  from = 1,
  to = 5,
}: GetGeoconvMultitermParams): Promise<GeoconvPoint[]> {
  const out: GeoconvPoint[] = []
  for (const pair of coords.split(';').map((s) => s.trim()).filter(Boolean)) {
    const [lonStr, latStr] = pair.split(',')
    const lon = Number(lonStr)
    const lat = Number(latStr)
    if (!Number.isNaN(lon) && !Number.isNaN(lat)) {
      const [x, y] = transformLegacyLonLat(lon, lat, from, to)
      out.push({ x, y, lat: y, lon: x })
    }
  }
  return out
}

/** WGS84 → BD09；`[0]`/`[1]` 为纬度/经度，同时带 `.lat` / `.lon` 与对象型历史一致 */
export function wgsTobd(lat: number | string, lon: number | string): LatLonTuple {
  const [bdLon, bdLat] = gcoord.transform(
    [Number(lon), Number(lat)],
    gcoord.WGS84,
    gcoord.BD09
  ) as [number, number]
  return tupleLatLon(bdLat, bdLon)
}

/** WGS84 → GCJ02；同上，元组与 `{ lat, lon }` 双形态 */
export function wgsToGcj(lat: number | string, lon: number | string): LatLonTuple {
  const [gcjLon, gcjLat] = gcoord.transform(
    [Number(lon), Number(lat)],
    gcoord.WGS84,
    gcoord.GCJ02
  ) as [number, number]
  return tupleLatLon(gcjLat, gcjLon)
}
